import type { AuthResponse } from "./types";

const ACCESS_KEY = "versecomms.accessToken";
const REFRESH_KEY = "versecomms.refreshToken";
const LOAD_CACHE_PREFIX = "versecomms.loadCache.";
const localPreview = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE = (import.meta.env.VITE_API_BASE || (localPreview ? "https://versecomms.online" : window.location.origin)).replace(/\/+$/, "");

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: Record<string, unknown>;

  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = typeof body?.error === "string" ? body.error : undefined;
  }
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY) || "";
}

export function saveAuth(auth: AuthResponse) {
  localStorage.setItem(ACCESS_KEY, auth.accessToken);
  localStorage.setItem(REFRESH_KEY, auth.refreshToken);
}

export function clearAuth() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  Object.keys(localStorage)
    .filter((key) => key.startsWith(LOAD_CACHE_PREFIX))
    .forEach((key) => localStorage.removeItem(key));
}

async function parseResponse(response: Response) {
  const type = response.headers.get("content-type") || "";
  return type.includes("application/json") ? response.json() : response.text();
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken })
  });
  if (!response.ok) {
    clearAuth();
    return false;
  }
  const result = await response.json();
  localStorage.setItem(ACCESS_KEY, result.accessToken);
  return true;
}

export async function api<T>(
  path: string,
  init: RequestInit & { anonymous?: boolean; retry?: boolean } = {}
): Promise<T> {
  const { anonymous = false, retry = true, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  const isForm = requestInit.body instanceof FormData;
  if (!isForm && requestInit.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!anonymous) {
    const token = getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(`${API_BASE}${path.startsWith("/") ? path : `/${path}`}`, {
    ...requestInit,
    headers
  });
  if (response.status === 401 && retry && !anonymous && (await refreshAccessToken())) {
    return api<T>(path, { ...init, retry: false });
  }
  const body = await parseResponse(response);
  if (response.ok && response.headers.get("content-type")?.includes("text/html")) {
    throw new ApiError("The server returned a webpage where API data was expected.", 502);
  }
  if (!response.ok) {
    const payload = typeof body === "object" && body ? body as Record<string, unknown> : undefined;
    const message = String(payload?.message || payload?.error || body || "Request failed");
    throw new ApiError(message, response.status, payload);
  }
  return body as T;
}

export function upload<T>(path: string, form: FormData) {
  return api<T>(path, { method: "POST", body: form });
}

export function websocketUrl() {
  const base = API_BASE.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
  return `${base}/ws?token=${encodeURIComponent(getAccessToken())}`;
}
